import { useCallback, useEffect, useRef, useState } from "react";
import { logError } from "utils/error";
import constants from "constants/constants";
import { notification_color_keys, useNotifications } from "utils/hooks";
import {
  getShowListingRatings,
  updateShowListingRatings,
} from "features/AdminPanel/modules/utils/AdminPanel.utils";
import { OFFERINGS_SECTION_MESSAGES } from "../../../sidebar.constant";

/**
 * Reads/writes the account-level show_listing_ratings setting and keeps the
 * misc-data copy (used by the classic template + Save payload) in sync.
 */
export const useShowListingRatingsToggle = ({
  updateData,
  miscKey,
  miscValue,
}) => {
  const { notify } = useNotifications();
  const [account_show_ratings, set_account_show_ratings] = useState(null);
  const [isShowRatingsLoading, setIsShowRatingsLoading] = useState(true);
  const [isShowRatingsUpdating, setIsShowRatingsUpdating] = useState(false);
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setIsShowRatingsLoading(true);
    getShowListingRatings()
      .then((value) => {
        if (cancelled) return;
        set_account_show_ratings(value);
        updateData(value, miscKey);
      })
      .catch((error) => {
        if (cancelled) return;
        logError({
          error,
          details: error,
          occuredAt: OFFERINGS_SECTION_MESSAGES.SHOW_RATINGS_ERROR_LOCATION,
          when: OFFERINGS_SECTION_MESSAGES.SHOW_RATINGS_FETCH_WHEN,
        });
        notify(constants.error_message, notification_color_keys.error);
      })
      .finally(() => {
        if (!cancelled) setIsShowRatingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miscKey]);

  const handleShowRatingsToggle = useCallback(
    (_event, checked) => {
      if (isUpdatingRef.current) return;

      const prevAccount = account_show_ratings;
      const prevMisc = miscValue;
      const current = account_show_ratings ?? prevMisc ?? false;
      const next = typeof checked === "boolean" ? checked : !current;

      set_account_show_ratings(next);
      // Misc copy feeds the classic template + Save payload.
      updateData(next, miscKey);

      isUpdatingRef.current = true;
      setIsShowRatingsUpdating(true);
      updateShowListingRatings(next)
        .catch((error) => {
          logError({
            error,
            details: error,
            occuredAt: OFFERINGS_SECTION_MESSAGES.SHOW_RATINGS_ERROR_LOCATION,
            when: OFFERINGS_SECTION_MESSAGES.SHOW_RATINGS_UPDATE_WHEN,
          });
          notify(constants.error_message, notification_color_keys.error);
          // Roll back so the UI never claims a value the API rejected.
          set_account_show_ratings(prevAccount);
          updateData(prevMisc, miscKey);
        })
        .finally(() => {
          isUpdatingRef.current = false;
          setIsShowRatingsUpdating(false);
        });
    },
    [account_show_ratings, miscKey, miscValue, notify, updateData]
  );

  return {
    account_show_ratings,
    handleShowRatingsToggle,
    isShowRatingsLoading,
    isShowRatingsUpdating,
  };
};
